import { useEffect, useState } from "react";
import { useToast } from "../../hooks/use-toast";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import {
  Search,
  Filter,
  MessageSquare,
  Phone,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  User,
  Package,
  Calendar,
  ChevronRight,
  HeadphonesIcon,
} from "lucide-react";
import SupportCaseModal from "./SupportCaseModal";
import Pagination from "../ui/pagination";
import { useAuth } from "../../lib/authContext";

interface SupportCase {
  id: string;
  case_number: string;
  order_id: string | null;
  customer_id: string;
  topic: string;
  subject: string;
  description: string | null;
  status: string;
  priority: string;
  assigned_to: string | null;
  department: string;
  callback_requested: boolean;
  callback_scheduled_at: string | null;
  callback_completed_at: string | null;
  callback_attempts: number;
  chatwoot_conversation_id: number | null;
  chatwoot_contact_id: number | null;
  resolution_rating: number | null;
  resolution_feedback: string | null;
  tags: string[] | null;
  metadata: any;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  closed_at: string | null;
  first_viewed_at?: string | null;
  // New fields from joins
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  tracking_id?: string;
}

export default function SupportCases() {
  const [cases, setCases] = useState<SupportCase[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    callbacksNeeded: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [topicFilter, setTopicFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [selectedCase, setSelectedCase] = useState<SupportCase | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;
  const { toast } = useToast();
  const { excelPermissions } = useAuth();

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    loadSupportCases();
  }, [
    currentPage,
    statusFilter,
    priorityFilter,
    topicFilter,
    dateFilter,
    searchTerm,
  ]);

  const loadStats = async () => {
    try {
      const response = await api.getSupportCaseStats();
      if (response && response.success) {
        setStats(response.stats);
      }
    } catch (err: any) {
      console.error("Failed to load stats:", err);
    }
  };

  const loadSupportCases = async () => {
    try {
      setIsLoading(true);

      const filters = {
        search: searchTerm || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        priority: priorityFilter !== "all" ? priorityFilter : undefined,
        topic: topicFilter !== "all" ? topicFilter : undefined,
        createdDate: dateFilter || undefined,
      };

      const response = await api.getSupportCases(
        currentPage,
        itemsPerPage,
        filters,
      );

      if (response && "error" in response) {
        const errorMessage =
          typeof response.error === "object" &&
          response.error !== null &&
          "message" in response.error
            ? String(response.error.message)
            : String(response.error);
        toast({
          title: "Error",
          description: errorMessage || "Failed to load support cases",
          variant: "destructive",
        });
      } else if (response && response.success) {
        setCases(response.results || []);
        setTotalCount(response.pagination?.totalCount || 0);
        setTotalPages(response.pagination?.totalPages || 1);
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to load support cases",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, priorityFilter, topicFilter, dateFilter, searchTerm]);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      open: {
        color: "bg-blue-100 text-blue-800 hover:bg-blue-100",
        icon: AlertCircle,
      },
      in_progress: {
        color: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
        icon: Clock,
      },
      resolved: {
        color: "bg-green-100 text-green-800 hover:bg-green-100",
        icon: CheckCircle,
      },
      closed: {
        color: "bg-gray-100 text-gray-800 hover:bg-gray-100",
        icon: XCircle,
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: "bg-gray-100 text-gray-800 hover:bg-gray-100",
      icon: AlertCircle,
    };

    const Icon = config.icon;

    return (
      <Badge
        className={`${config.color} flex items-center gap-1 cursor-default`}
      >
        <Icon size={12} />
        {status.replace("_", " ").charAt(0).toUpperCase() +
          status.slice(1).replace("_", " ")}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: "bg-gray-100 text-gray-800 hover:bg-gray-100",
      medium: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
      high: "bg-red-100 text-red-800 hover:bg-red-100",
      urgent: "bg-red-500 text-white hover:bg-red-500",
    };

    const color =
      priorityConfig[priority as keyof typeof priorityConfig] ||
      "bg-gray-100 text-gray-800 hover:bg-gray-100";

    return (
      <Badge className={`${color} cursor-default`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    );
  };

  const calculateTAT = (
    createdAt: string,
    status: string,
    updatedAt: string,
  ) => {
    const created = new Date(createdAt);
    const now = new Date();
    const updated = new Date(updatedAt);

    // For closed cases, calculate time from created to updated
    const endTime = status === "closed" ? updated : now;
    const diffMs = endTime.getTime() - created.getTime();

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    const remainingHours = diffHours % 24;

    let tatText = "";
    if (diffDays > 0) {
      tatText = `${diffDays}d ${remainingHours}h`;
    } else {
      tatText = `${diffHours}h`;
    }

    // Color coding based on age for open cases
    let colorClass = "text-gray-600";
    if (status !== "closed") {
      if (diffHours < 24) {
        colorClass = "text-green-600";
      } else if (diffHours < 48) {
        colorClass = "text-yellow-600";
      } else if (diffHours < 72) {
        colorClass = "text-orange-600";
      } else {
        colorClass = "text-red-600";
      }
    }

    return (
      <span className={`font-medium ${colorClass}`}>
        {status === "closed" ? "Closed in " : "Open for "}
        {tatText}
      </span>
    );
  };

  const formatTopicDisplay = (topic: string) => {
    return topic
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleViewCase = async (caseData: SupportCase) => {
    try {
      // Fire and forget; do not block UI
      void api.markSupportCaseViewed(caseData.id);
    } catch {}
    setSelectedCase(caseData);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCase(null);
    loadSupportCases(); // Reload to get any updates
    loadStats(); // Reload stats in case status changed
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";
    return new Date(date).toLocaleString();
  };

  const getStatsCards = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Total Cases
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Open
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.open}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              In Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {stats.inProgress}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Resolved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats.resolved}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Callbacks Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {stats.callbacksNeeded}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <HeadphonesIcon size={32} />
            Support Cases
          </h1>
          <p className="text-gray-600 mt-1">
            Manage customer support cases and inquiries
          </p>
        </div>
        {excelPermissions.canDownloadExcel && (
          <Button
            className="bg-green-500 text-white dark:bg-white dark:text-black"
            onClick={async () => {
              try {
                const filters = {
                  search: searchTerm || undefined,
                  status: statusFilter !== "all" ? statusFilter : undefined,
                  priority: priorityFilter !== "all" ? priorityFilter : undefined,
                  topic: topicFilter !== "all" ? topicFilter : undefined,
                  createdDate: dateFilter || undefined,
                };

                const response = await api.downloadSupportCasesExcel(filters);
                if (response?.success && response.data && response.filename) {
                  const link = document.createElement("a");
                  link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data}`;
                  link.download = response.filename;
                  link.click();
                } else {
                  toast({
                    title: "Error",
                    description:
                      response?.error || "Failed to download support cases Excel",
                    variant: "destructive",
                  });
                }
              } catch (err: any) {
                toast({
                  title: "Error",
                  description: err?.message || "Failed to download Excel",
                  variant: "destructive",
                });
              }
            }}
          >
            Download Excel
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      {getStatsCards()}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={16}
          />
          <Input
            placeholder="Search by case number, subject, customer name, phone, or tracking ID..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
        <Select value={topicFilter} onValueChange={setTopicFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by topic" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Topics</SelectItem>
            <SelectItem value="order_issue">Order Issue</SelectItem>
            <SelectItem value="payment_issue">Payment Issue</SelectItem>
            <SelectItem value="delivery_delay">Delivery Delay</SelectItem>
            <SelectItem value="wrong_item">Wrong Item</SelectItem>
            <SelectItem value="damaged_item">Damaged Item</SelectItem>
            <SelectItem value="refund_request">Refund Request</SelectItem>
            <SelectItem value="prescription_query">
              Prescription Query
            </SelectItem>
            <SelectItem value="product_inquiry">Product Inquiry</SelectItem>
            <SelectItem value="technical_issue">Technical Issue</SelectItem>
            <SelectItem value="account_issue">Account Issue</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={dateFilter}
          onChange={e => setDateFilter(e.target.value)}
          className="w-48"
          placeholder="Case created on"
        />
      </div>

      {/* Results count */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          Showing {cases.length} of {totalCount} cases
        </div>
        {totalPages > 1 && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      {/* Cases Table */}
      <div className="w-full bg-white dark:bg-gray-900 rounded-lg shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Case Number</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Topic</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>TAT</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cases.map(supportCase => (
              <TableRow key={supportCase.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{supportCase.case_number}</span>
                    {supportCase.callback_requested &&
                      !supportCase.callback_completed_at && (
                        <Phone size={14} className="text-orange-500" />
                      )}
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <div className="flex items-center gap-1 text-sm font-medium">
                      <User size={14} />
                      {supportCase.customer_name || "Unknown"}
                    </div>
                    {supportCase.customer_phone && (
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <Phone size={10} />
                        {supportCase.customer_phone}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>{formatTopicDisplay(supportCase.topic)}</TableCell>
                <TableCell>{getStatusBadge(supportCase.status)}</TableCell>
                <TableCell>{getPriorityBadge(supportCase.priority)}</TableCell>
                <TableCell>
                  {calculateTAT(
                    supportCase.created_at,
                    supportCase.status,
                    supportCase.updated_at,
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-sm">
                    <Calendar size={14} />
                    {formatDate(supportCase.created_at).split(",")[0]}
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewCase(supportCase)}
                  >
                    <MessageSquare size={14} className="mr-1" />
                    View
                    <ChevronRight size={14} className="ml-1" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {cases.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <HeadphonesIcon size={48} className="mx-auto mb-4 text-gray-400" />
            <p>No support cases found</p>
          </div>
        )}
      </div>

      {/* Bottom pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Modal */}
      {isModalOpen && selectedCase && (
        <SupportCaseModal
          supportCase={selectedCase}
          onClose={handleCloseModal}
          onUpdate={loadSupportCases}
        />
      )}
    </div>
  );
}
